import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';

export interface GenomeGraphHandle {
  centerOnArtist: (userId: string) => void;
}

interface GenomePoint {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  x: number;
  y: number;
  sampleCount: number;
}

interface GenomeNeighbor {
  userId: string;
  similarity: number;
  concordance: number | null;
}

interface GenomeGraphProps {
  points: GenomePoint[];
  neighbors: GenomeNeighbor[];
  anchorUserId: string | null;
  pinnedUserIds: string[];
  axis: string;
  onSelectArtist: (userId: string, options: { shiftKey: boolean }) => void;
}

const COLOR_ANCHOR = '#7ee787';
const COLOR_PINNED = 'rgba(192, 132, 252, 0.95)';
const COLOR_NEIGHBOR = 'rgba(100, 210, 255, 0.9)';
const COLOR_DEFAULT = 'rgba(100, 210, 255, 0.15)';
const COLOR_CONCORDANCE_3 = 'rgba(240, 80, 80, 0.85)';
const COLOR_CONCORDANCE_2 = 'rgba(242, 162, 60, 0.7)';
const COLOR_CONCORDANCE_1 = 'rgba(121, 192, 255, 0.5)';

const PCA_SCALE = 2000;
const NODE_SIZE = 2;
const POINTER_AREA_SIZE = 6;

const GenomeGraph = forwardRef<GenomeGraphHandle, GenomeGraphProps>(function GenomeGraph(
  { points, neighbors, anchorUserId, pinnedUserIds, axis, onSelectArtist },
  ref
) {
  const fgRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const pointIndex = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    for (const p of points) map.set(p.userId, { x: p.x * PCA_SCALE, y: p.y * PCA_SCALE });
    return map;
  }, [points]);

  useImperativeHandle(
    ref,
    () => ({
      centerOnArtist: (userId: string) => {
        const pos = pointIndex.get(userId);
        if (!pos || !fgRef.current) return;
        fgRef.current.centerAt(pos.x, pos.y, 600);
        fgRef.current.zoom(2.5, 600);
      },
    }),
    [pointIndex]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const neighborSet = useMemo(() => new Set(neighbors.map((n) => n.userId)), [neighbors]);
  const pinnedSet = useMemo(() => new Set(pinnedUserIds), [pinnedUserIds]);

  const concordanceMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const n of neighbors) {
      if (n.concordance != null) map.set(n.userId, n.concordance);
    }
    return map;
  }, [neighbors]);

  const graphData = useMemo(() => {
    const nodes = points.map((p) => ({
      id: p.userId,
      displayName: p.displayName,
      fx: p.x * PCA_SCALE,
      fy: p.y * PCA_SCALE,
    }));

    const links = anchorUserId
      ? neighbors.map((n) => ({
          source: anchorUserId,
          target: n.userId,
          similarity: n.similarity,
          concordance: n.concordance,
        }))
      : [];

    return { nodes, links };
  }, [points, neighbors, anchorUserId]);

  // Nodes are pinned via fx/fy (set where graphData is built) and cooldownTicks
  // is 0 on ForceGraph2D below, so d3's force simulation never runs. No need
  // to manually null out the 'charge', 'center', and 'link' forces here.

  useEffect(() => {
    if (fgRef.current && points.length > 0) {
      setTimeout(() => fgRef.current?.zoomToFit(400, 50), 100);
    }
  }, [points]);

  const handleNodeClick = useCallback(
    (node: any, event: MouseEvent) => {
      if (node?.id) onSelectArtist(node.id, { shiftKey: event.shiftKey });
    },
    [onSelectArtist]
  );

  const nodeCanvasObject = useCallback(
    (node: any, ctx: CanvasRenderingContext2D) => {
      const isAnchor = node.id === anchorUserId;
      const isPinned = pinnedSet.has(node.id);
      const isNeighbor = neighborSet.has(node.id);

      let fill = COLOR_DEFAULT;
      if (isAnchor) {
        fill = COLOR_ANCHOR;
      } else if (isPinned) {
        fill = COLOR_PINNED;
      } else if (isNeighbor && axis === 'COMBINED') {
        const c = concordanceMap.get(node.id) ?? 1;
        fill = c >= 3 ? COLOR_CONCORDANCE_3 : c >= 2 ? COLOR_CONCORDANCE_2 : COLOR_CONCORDANCE_1;
      } else if (isNeighbor) {
        fill = COLOR_NEIGHBOR;
      }

      ctx.beginPath();
      ctx.arc(node.x, node.y, NODE_SIZE, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
    },
    [anchorUserId, pinnedSet, neighborSet, concordanceMap, axis]
  );

  const nodePointerAreaPaint = useCallback((node: any, color: string, ctx: CanvasRenderingContext2D) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(node.x, node.y, POINTER_AREA_SIZE, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  const linkColor = useCallback(
    (link: any) => {
      if (axis === 'COMBINED' && link.concordance != null) {
        return link.concordance >= 3
          ? COLOR_CONCORDANCE_3
          : link.concordance >= 2
            ? COLOR_CONCORDANCE_2
            : COLOR_CONCORDANCE_1;
      }
      return 'rgba(121, 192, 255, 0.3)';
    },
    [axis]
  );

  const nodeLabel = useCallback((node: any) => node.displayName ?? node.id, []);

  return (
    <div ref={containerRef} className="h-full w-full overflow-hidden">
      {size.width > 0 && size.height > 0 && points.length > 0 && (
        <ForceGraph2D
          ref={fgRef}
          width={size.width}
          height={size.height}
          graphData={graphData}
          nodeCanvasObject={nodeCanvasObject}
          nodePointerAreaPaint={nodePointerAreaPaint}
          nodeLabel={nodeLabel}
          onNodeClick={handleNodeClick}
          linkColor={linkColor}
          linkWidth={1}
          cooldownTicks={0}
          enableNodeDrag={false}
          backgroundColor="transparent"
        />
      )}
    </div>
  );
});

export default GenomeGraph;
